import React from 'react';

import {
  DateField,
  NumberField,
  RecordContextProvider,
  SelectField,
  TextField,
  usePermissions,
} from 'react-admin';

import {
  Box,
  Card,
  CardHeader,
  Grid,
  CardContent,
  Typography,
  Divider,
  Alert,
} from '@mui/material';

import useEntityData from '../../../../hooks/useEntityData';

import PurchaseButton from '../../../layout/button/PurchaseButton';

import actions, { Actions } from '../../../../constants/actions';
import { EntityTypes } from '../../../../constants/entityTypes';
import CurrencyField from '../../../utils/CurrencyField';
import hasAccess from '../../../../utilities/hasAccess';
import PermissionMissing from '../../../utils/PermissionMissing';
import NoResults from '../../../layout/NoResults';
import companyAuditStatus from '../../../../constants/companyAuditorStatuses';

type ICreditReport = {
  id: string;
  createdAt: Date;
  entityType: EntityTypes;
  apiSearchTerms: {
    [searchTerm : string]: string | number | undefined;
  };
  companyId: string;
  rating: string;
  ratingText: string;
  commentary: {
    commentaryCode: string;
    commentaryText: string;
    commentaryType: string; // 1 positive, 0 neutral, -1 negative
  }[];
  riskPrognosis: number;
  riskCheckAuditorCode: number;
  limit: number;
  limitCurrency: string;
  ratingHistory: {
    rating: string;
    ratingText: string;
    ratingDate: string;
  }[];
  companyInternalWarning: boolean; //  on the internal warning list (1 = Yes, 0 = No)
  numberPaymentRemark: number;
  sumPaymentRemark: number;
  numberPaymentApplications: number;
  sumPaymentApplications: number;
  numberPublicPaymentRemark: number;
  sumPublicPaymentRemark: number;
  numberPublicPaymentRemarkLastYear: number;
  numberPrivatePaymentRemark: number;
  sumPrivatePaymentRemark: number;
  numberPrivatePaymentRemarkLastYear: number;
  detailedPublicPaymentRemarkLastYear: {
    companyId: string;
    createdDate: string;
    amount: number;
    type: string;
    typeText: string;
    term: string;
    comments: string;
  }[];
  detailedPrivatePaymentRemarkLastYear: {
    companyId: string;
    createdDate: string;
    amount: number;
    type: string;
    typeText: string;
    creditorId: string;
    creditorName: string;
    creditorForeign: boolean;
    comments: string;
  }[];
  detailedPaymentApplications: {
    companyId: string;
    createdDate: string;
    amount: number;
    type: string;
    typeText: string;
    creditorId: string;
    creditorName: string;
    creditorForeign: string;
    comments: string;
  }[];
  detailedPublicPaymentRemark: {
    companyId: string;
    createdDate: string;
    amount: number;
    type: string;
    typeText: string;
    term: string;
    comments: string;
  }[];
  detailedPrivatePaymentRemark: {
    companyId: string;
    createdDate: string;
    amount: number;
    type: string;
    typeText: string;
    creditorId: string;
    creditorName: string;
    creditorForeign: boolean;
    comments: string;
  }[];
  kfDebt: { // Kronofogden
    sum: number;
    date: string;
    nrPublic: number;
    nrPrivate: number;
    sumPublic: number;
    sumPrivate: number;
    historicalDebtDetails: {
      date: string;
      numberPublic: string;
      numberPrivate: string;
      totalBalance: string;
    }[];
    distraint: boolean;
    distraintRepossessionClaims: {
      companyId: string;
      createdDate: string;
      type: string;
      typeText: string;
      comments: string;
    }[];
  };
  bankruptcy: {
    type: string; // Current status, type
    date: string; // Current status, date for last change
    text: string; // Current status, text
    trustee: {
      name: string;
      companyName: string;
      phone: string;
      phoneCentral: string;
      visitAddress: string;
      boxAddress: string;
      postCode: string;
      postTown: string;
    };
  };
  payData: {
    avgNrDaysBeyondTerms: number;
    avgValueOfAvailableInvoices: number;
    nrAvailableInvoices: number;
    nrPaidInvoices: number;
    nrOutstandingInvoices: number;
    paidInvoicesDetails: {
      withinTerms: number;
      beyondTerms1to5Days: number;
      beyondTerms6to15Days: number;
      beyondTerms16t030Days: number;
      beyondTerms31t060Days: number;
      beyondTerms61OrMoreDays: number;
    }[];
    outstandingInvoicesDetails: {
      withinTerms: number;
      beyondTerms1to5Days: number;
      beyondTerms6to15Days: number;
      beyondTerms16t030Days: number;
      beyondTerms31t060Days: number;
      beyondTerms61OrMoreDays: number;
    }[];
  };
  errorMessages?: {
    code: string;
    description: string;
  }[];
}

const CreditReport = () => {
  const {
    data,
    handlePurchase,
    isLoading,
  } = useEntityData<ICreditReport>({
    source: 'credit',
    getAction: actions.BUSINESS_GET_CREDIT,
  });
  const { permissions } = usePermissions<Actions[]>();

  if (!hasAccess(permissions, actions.BUSINESS_GET_CREDIT)) {
    return (
      <Card variant="outlined">
        <Box padding={4} display="flex" alignItems="center" width="100%" flexDirection="column">
          <PermissionMissing />
        </Box>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card variant="outlined">
        <Box padding={4} display="flex" alignItems="center" width="100%" flexDirection="column">
          <NoResults />
          <PurchaseButton action={actions.BUSINESS_CREATE_CREDIT} loading={isLoading} label="Get company rating" empty onClick={handlePurchase} />
        </Box>
      </Card>
    );
  }

  return (
    <RecordContextProvider value={data}>
      <Grid container spacing={6}>
        <Grid size={12}>
          <Card variant="outlined">
            <CardHeader
              title="Credit ratings"
              subheader={(
                <Box display="flex" alignItems="baseline">
                  <Typography>Latest update:</Typography>
                  <DateField sx={{ marginLeft: 1, fontWeight: 'bold' }} source="createdAt" emptyText="-" showTime />
                </Box>
              )}
              action={(
                <PurchaseButton
                  loading={isLoading}
                  label="Get company rating"
                  onClick={handlePurchase}
                  action={actions.BUSINESS_CREATE_CREDIT}
                />
              )}
            />
            <Divider />
            <CardContent>
              {
                data?.errorMessages && (data?.errorMessages.map((error) => (
                  <Grid margin={1} container justifyContent="center" key={error.code}>
                    <Alert severity="warning">{error.description}</Alert>
                  </Grid>
                ))
                )
              }
              <Grid container spacing={6}>
                <Grid size={4}>
                  <Box display="flex" gap={1} alignItems="baseline">
                    <Typography variant="smallTitle">Credit rating:</Typography>
                    <TextField source="rating" emptyText="-" />
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box display="flex" gap={1} alignItems="baseline">
                    <Typography variant="smallTitle">Credit rating description:</Typography>
                    <TextField source="ratingText" emptyText="-" />
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box display="flex" gap={1} alignItems="baseline">
                    <Typography variant="smallTitle">Probability of default:</Typography>
                    <NumberField source="riskPrognosis" emptyText="-" />
                    <Typography>%</Typography>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box display="flex" gap={1} alignItems="baseline">
                    <Typography variant="smallTitle">Credit limit:</Typography>
                    <CurrencyField source="limit" currencySource="limitCurrency" />
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box display="flex" gap={1} alignItems="baseline">
                    <Typography variant="smallTitle">Auditor status:</Typography>
                    <SelectField source="riskCheckAuditorCode" choices={companyAuditStatus} emptyText="-" />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </RecordContextProvider>
  );
};

export default CreditReport;
